import { DropdownMenuItem } from "@/components/core/actions/dropdown-menu";
import { Button } from "@/components/core/buttons/button";
import DataTable, { type ColumnDef } from "@/components/core/data-table";
import { CreateInvoicePrintTemplateModal } from "@/components/invoice-print-templates/create-invoice-print-template-modal";
import { Content } from "@/components/layouts/content";
import {
	invoicePrintTemplatesQueryOptions,
	useInvoicePrintTemplateActivateMutation,
	useInvoicePrintTemplateDeleteMutation,
} from "@/features/dashboard/api/queries/invoice-print-template-query";
import type { InvoicePrintTemplate } from "@/features/dashboard/types/invoice-print-template";
import { IconCheck, IconPlus } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import clsx from "clsx";
import { useState } from "react";
import { toast } from "sonner";

const columns: ColumnDef<InvoicePrintTemplate>[] = [
	{
		id: "name",
		label: "Template",
		cellClassName: "min-w-[260px]",
		cell: ({ data }) => (
			<div className="flex items-center gap-3">
				{data.background_url ? (
					<img src={data.background_url} alt="" className="w-8 h-10 object-cover rounded border border-border/50" />
				) : (
					<div className="w-8 h-10 rounded border border-dashed border-border/50 bg-slate-50" />
				)}
				<span className="font-medium text-foreground-dark">{data.name}</span>
			</div>
		),
	},
	{
		id: "page_size",
		label: "Page",
		cellClassName: "min-w-[120px]",
		cell: ({ data }) => (
			<span className="text-sm text-foreground/70">
				{data.page_size.toUpperCase()} · {data.orientation}
			</span>
		),
	},
	{
		id: "is_active",
		label: "Status",
		cellClassName: "min-w-[80px]",
		cell: ({ data }) => (
			<span className={clsx("px-2 py-1 rounded-full text-xs font-medium", data.is_active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800")}>
				{data.is_active ? "Active" : "Inactive"}
			</span>
		),
	},
	{
		id: "updated_at",
		label: "Updated",
		cellClassName: "min-w-[120px]",
		cell: ({ data }) => <span className="text-gray-900">{data.updated_at ? new Date(data.updated_at).toLocaleDateString() : "-"}</span>,
	},
];

export const Route = createFileRoute("/_dashboard/invoice-print-templates")({
	component: Page,
});

function Page() {
	const navigate = useNavigate();
	const [isCreateOpen, setIsCreateOpen] = useState(false);
	const templatesQuery = useQuery(invoicePrintTemplatesQueryOptions());
	const activateMutation = useInvoicePrintTemplateActivateMutation();
	const deleteMutation = useInvoicePrintTemplateDeleteMutation();

	const templates = templatesQuery.data?.data ?? [];

	const goToEditor = (template: InvoicePrintTemplate) => {
		navigate({ to: "/invoice-print-templates/$templateId", params: { templateId: template.id } });
	};

	const handleActivate = async (template: InvoicePrintTemplate) => {
		await activateMutation.mutateAsync(template.id);
		toast.success(`"${template.name}" is now the active template`);
	};

	const handleDelete = async (template: InvoicePrintTemplate) => {
		if (template.is_active) {
			toast.error("Activate another template before deleting this one");
			return;
		}
		await deleteMutation.mutateAsync(template.id);
		toast.success("Template deleted");
	};

	return (
		<Content pageName="Invoice Print Templates">
			<div className="flex flex-col w-full max-w-base p-6 gap-6">
				<div className="flex flex-row items-center justify-between w-full gap-4">
					<div className="inline-flex flex-col w-auto">
						<span className="text-xl text-foreground-dark font-semibold">Invoice Print Templates</span>
						<span className="text-base text-foreground/70">Configure the layouts used to render invoice request PDFs</span>
					</div>
					<Button onClick={() => setIsCreateOpen(true)}>
						<IconPlus size={18} stroke={2} />
						<span>New Template</span>
					</Button>
				</div>
				<DataTable
					className="border-collapse"
					data={templates}
					columns={columns}
					stickyHead={false}
					stickyCol={false}
					showBorder={true}
					compact={true}
					showToolbar={false}
					onEdit={goToEditor}
					onDelete={handleDelete}
					options={(template) =>
						!template.is_active && (
							<DropdownMenuItem onSelect={() => handleActivate(template)}>
								<div className="flex flex-row items-center w-full py-1 px-2 gap-2">
									<IconCheck size={16} stroke={2} />
									<span>Activate</span>
								</div>
							</DropdownMenuItem>
						)
					}
				/>
			</div>
			<CreateInvoicePrintTemplateModal
				isOpen={isCreateOpen}
				defaults={templatesQuery.data?.defaults}
				onClose={() => setIsCreateOpen(false)}
				onCreated={(id) => navigate({ to: "/invoice-print-templates/$templateId", params: { templateId: id } })}
			/>
		</Content>
	);
}
